// CompanyFieldBindings — the companyField element's `bindingList` editor, inline
// in the Basic-options panel (no drawer, unlike the dataSource field's). A
// collapsible sub-section in the same shape as checkList's "Answer choices":
// with the same table shape as the dataSource drawer's "Bind Field to Data
// Source" section: a header row, one row per binding (field · column · trash),
// then a two-up draft of Selects and a full-width Add binding button.
//
// Labels ellipsise and carry a `title` — the properties panel is only ~280px, so
// the two columns are narrow. Its own component because the draft row and the
// collapse need state, which a render helper can't hold.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, IconButton, SelectDropdown } from '@/components/common';
import type { DataSourceColumn } from '@/api/endpoints/datasourceApi';
import type { DataSourceBinding } from './utils/formElementDefaults';
import type { BindableField } from './utils/builderModel';

interface CompanyFieldBindingsProps {
  bindings: DataSourceBinding[];
  /** Form fields a binding can target (text/number/checkList across the form). */
  bindableFields: BindableField[];
  /** Columns of the picked company field's data source. */
  columns: DataSourceColumn[];
  onChange: (next: DataSourceBinding[]) => void;
}

const CompanyFieldBindings = ({
  bindings,
  bindableFields,
  columns,
  onChange,
}: CompanyFieldBindingsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [draftField, setDraftField] = useState('');
  const [draftColumn, setDraftColumn] = useState('');

  const fieldTitle = (id: string) => bindableFields.find((f) => f.id === id)?.title ?? id;
  const columnName = (id: string) => columns.find((c) => c.columnId === id)?.columnName ?? id;

  // A field can only be filled from one column, so bound ones drop out.
  const boundIds = new Set(bindings.map((b) => b.fieldId));
  const availableFields = bindableFields.filter((f) => !boundIds.has(f.id));

  // Nothing to bind against until a company field names a data source.
  const ready = columns.length > 0;
  const canAdd = ready && Boolean(draftField) && Boolean(draftColumn);

  const add = () => {
    if (!canAdd) return;
    onChange([...bindings, { fieldId: draftField, columnId: draftColumn }]);
    setDraftField('');
    setDraftColumn('');
  };

  const placeholder = { value: '', label: t('forms_builder_props_cf_select') };

  return (
    <section className='form-builder-props__answers'>
      <button
        type='button'
        className='form-builder-props__answers-head'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {t('forms_builder_props_cf_bindings')}
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={15} />
      </button>

      {open && (
        <div className='form-builder-props__answers-body'>
          <div className='form-builder-props__field-row'>
            <span className='form-builder-props__label'>
              {t('forms_builder_props_cf_bindings_label')}
            </span>
            <span className='form-builder-props__label-note'>
              {t('forms_builder_props_cf_bindings_note')}
            </span>
          </div>

          {!ready && (
            <p className='form-builder-props__binding-empty'>
              {t('forms_builder_props_cf_columns_empty')}
            </p>
          )}

          {/* Same table shape as the dataSource drawer's "Bind Field to Data
              Source": header, rows with a trash button, then a two-up draft and
              a full-width Add. Labels ellipsise (`title` holds the full text) —
              the panel is only ~280px. */}
          {ready && bindings.length > 0 && (
            <div
              className='form-builder-props__binding-row form-builder-props__binding-row--head'
              aria-hidden='true'
            >
              <span>{t('forms_builder_ds_col_field')}</span>
              <span>{t('forms_builder_ds_col_column')}</span>
              <span />
            </div>
          )}

          {bindings.map((b) => (
            <div key={`${b.fieldId}-${b.columnId}`} className='form-builder-props__binding-row'>
              <span className='form-builder-props__binding-cell' title={fieldTitle(b.fieldId)}>
                {fieldTitle(b.fieldId)}
              </span>
              <span
                className='form-builder-props__binding-cell form-builder-props__binding-cell--muted'
                title={columnName(b.columnId)}
              >
                {columnName(b.columnId)}
              </span>
              <IconButton
                icon='trash-2'
                variant='ghost'
                size='sm'
                className='form-builder-props__binding-remove'
                aria-label={t('forms_builder_props_cf_binding_remove')}
                onClick={() =>
                  onChange(
                    bindings.filter((x) => !(x.fieldId === b.fieldId && x.columnId === b.columnId)),
                  )
                }
              />
            </div>
          ))}

          {ready && (
            <>
              <div className='form-builder-props__binding-pair'>
                <SelectDropdown
                  size='sm'
                  aria-label={t('forms_builder_props_cf_binding_field')}
                  value={draftField}
                  options={[
                    placeholder,
                    ...availableFields.map((f) => ({ value: f.id, label: f.title })),
                  ]}
                  onChange={(next) => setDraftField(next)}
                  portal
                />
                <SelectDropdown
                  size='sm'
                  aria-label={t('forms_builder_props_cf_binding_column')}
                  value={draftColumn}
                  options={[
                    placeholder,
                    ...columns.map((c) => ({ value: c.columnId, label: c.columnName })),
                  ]}
                  onChange={(next) => setDraftColumn(next)}
                  portal
                />
              </div>
              <Button
                size='sm'
                iconStart='plus'
                className='form-builder-props__binding-add'
                disabled={!canAdd}
                onClick={add}
              >
                {t('forms_builder_props_cf_binding_add')}
              </Button>
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default CompanyFieldBindings;
